import React, { useEffect, useState } from 'react';
import { 
  Drawer, 
  Paper, 
  IconButton, 
  Button, 
  TextField, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Menu,
  MenuItem
} from '@material-ui/core';
import { Star, StarBorder, MoreVert, Edit, Delete } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import { http, localstorage, AlertService } from '../../fx-core';

type Review = {
  id: number;
  rating: number;
  title: string;
  description: string;
  stallId: number;
  stallName?: string;
  reviewDate?: string;
  createdDate?: string;
};

type EditData = {
  rating: number;
  title: string;
  text: string;
};

function StarRatingEdit({
  value,
  onChange
}: {
  value: number,
  onChange: (v: number) => void
}) {
  return (
    <Box display="flex" alignItems="center" style={{ gap: 2 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box key={star} position="relative" display="inline-block">
          <span
            style={{
              cursor: 'pointer',
              display: 'inline-block',
              width: 32,
              height: 32
            }}
            onClick={() => onChange(star)}
            title={`${star} star`}
          >
            <Star style={{ color: value >= star ? "#FFD700" : "#e0e0e0", fontSize: 32 }} />
          </span>
          {star !== 1 && (
            <span
              style={{
                position: "absolute",
                top: 0, left: 0,
                width: 16, height: 32,
                cursor: "pointer",
                zIndex: 5,
                display: "inline-block"
              }}
              onClick={() => onChange(star - 0.5)}
              title={`${star - 0.5} stars`}
            >
              <span style={{
                position: "absolute", 
                left: 0, top: 0, width: "100%", height: "100%",
                pointerEvents: "none"
              }} />
            </span>
          )}
          {value === star - 0.5 && (
            <Star style={{
              color: "#FFD700", fontSize: 32,
              position: "absolute", left: 0, top: 0,
              clipPath: "inset(0 50% 0 0)",
              pointerEvents: "none"
            }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

const UserReviewsSidebar: React.FC<{
  open: boolean;
  onClose: () => void;
  userId: number;
}> = ({ open, onClose, userId }) => {
  const [userReviews, setUserReviews] = useState<Review[]>([]);
  const [editReviewId, setEditReviewId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditData>({ rating: 0, title: '', text: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReviewId, setDeleteReviewId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  useEffect(() => {
    if (open && userId) {
      fetchUserReviews();
    }
  }, [open, userId]);

  const fetchUserReviews = () => {
    const apiOptions = {
      url: `stallreviews/user/${userId}`
    };
    http.get(apiOptions).then(response => {
      if (response.data.success) {
        const sortedReviews = (response.data.reviews || []).sort((a: Review, b: Review) => {
          const dateA = new Date(a.createdDate || 0).getTime();
          const dateB = new Date(b.createdDate || 0).getTime();
          return dateB - dateA;
        });
        setUserReviews(sortedReviews);
      }
    }).catch(error => {
      console.error('Error fetching reviews:', error);
      AlertService.showMsg({ message: 'Failed to load reviews' });
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, review: Review) => {
    setAnchorEl(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReview(null);
  };

  const startEdit = (review: Review) => {
    setEditReviewId(review.id);
    setEditData({
      rating: review.rating,
      title: review.title || '',
      text: review.description || ''
    });
    handleMenuClose();
  };

  const cancelEdit = () => {
    setEditReviewId(null);
    setEditData({ rating: 0, title: '', text: '' });
  };

  const saveEdit = () => {
    if (editData.title.length > 200) {
      AlertService.showMsg({ message: "Review title must be at most 200 characters." });
      return;
    }
    if (editData.text.length > 2000) {
      AlertService.showMsg({ message: "Review text must be at most 2000 characters." });
      return;
    }
    if (editData.rating < 1 || editData.rating > 5) {
      AlertService.showMsg({ message: "Rating must be between 1 and 5." });
      return;
    }

    setLoading(true);
    const apiOptions = {
      url: `stallreviews/${editReviewId}`,
      data: {
        userId: Number(localstorage.getItem("userId")),
        rating: Number(editData.rating),
        title: editData.title,
        description: editData.text
      }
    };
    http.put(apiOptions).then(response => {
      if (response.data.success) {
        AlertService.showSuccessMsg('Review updated successfully!');
        setUserReviews(reviews =>
          reviews.map(r => r.id === editReviewId
            ? { ...r, rating: editData.rating, title: editData.title, description: editData.text }
            : r)
        );
        cancelEdit();
      }
    }).catch(error => {
      let errMsg = 'Failed to update review';
      if (error.response?.data?.error) {
        errMsg = error.response.data.error;
      }
      AlertService.showMsg({ message: errMsg });
      console.error('Update error:', error);
    }).finally(() => {
      setLoading(false);
    });
  };

  const openDeleteDialog = (review: Review) => {
    setDeleteReviewId(review.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteReviewId(null);
  };


  const confirmDelete = async () => {
  if (!deleteReviewId) return;
  setLoading(true);
  try {
    const apiOptions: any = {
      url: `stallreviews/${deleteReviewId}`,
      data: {} // Or omit 'data' entirely!
    };
    await http.delete(apiOptions);
    AlertService.showSuccessMsg('Review deleted successfully!');
    setUserReviews(reviews => reviews.filter(r => r.id !== deleteReviewId));
    closeDeleteDialog();
  } catch (error) {
    let errMsg = 'Failed to delete review';
    if (error.response?.data?.error) {
      errMsg = error.response.data.error;
    }
    AlertService.showMsg({ message: errMsg });
    console.error('Delete error:', error);
  } finally {
    setLoading(false);
  }
};


  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ 
          style: { 
            width: '40vw', 
            maxWidth: '600px',
            padding: 0
          } 
        }}
      >
        {/* Orange header with title and review count */}
        <div style={{ 
          background: '#FF9800',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
              My Reviews
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
              {userReviews.length} {userReviews.length === 1 ? 'review' : 'reviews'}
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small" style={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </div>
        {/* Reviews List - Scrollable */}
        <div style={{ 
          overflowY: 'auto', 
          flex: 1, 
          padding: '24px', 
          background: '#f5f5f5'
        }}>
          {userReviews.length === 0 && (
            <Typography style={{ textAlign: 'center', marginTop: '32px', color: '#999' }}>
              No reviews found.
            </Typography>
          )}
          {userReviews.map(review =>
            editReviewId === review.id ? (
              <Paper key={review.id} style={{ margin: '0 0 16px 0', padding: '16px' }} elevation={2}>
                <Typography variant="subtitle1" style={{ marginBottom: '16px', fontWeight: 'bold' }}>
                  Edit Review for {review.stallName || `Stall #${review.stallId}`}
                </Typography>
                <Box mb={2}>
                  <Typography variant="body2" style={{ marginBottom: '8px', fontWeight: 500 }}>Rating</Typography>
                  <StarRatingEdit value={editData.rating} onChange={v => setEditData(data => ({ ...data, rating: v }))} />
                  <Typography variant="body2" style={{ marginTop: '8px', color: '#666' }}>
                    {editData.rating.toFixed(1)} stars
                  </Typography>
                </Box>
                <TextField
                  label="Title"
                  value={editData.title}
                  onChange={e => setEditData(data => ({ ...data, title: e.target.value }))}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${editData.title.length}/200 characters`}
                  style={{ margin: '8px 0' }}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Review"
                  multiline
                  rows={5}
                  value={editData.text}
                  onChange={e => setEditData(data => ({ ...data, text: e.target.value }))}
                  inputProps={{ maxLength: 2000 }}
                  helperText={`${editData.text.length}/2000 characters`}
                  style={{ margin: '8px 0' }}
                  fullWidth
                  variant="outlined"
                />
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                  <Button 
                    color="primary" 
                    variant="contained" 
                    onClick={saveEdit}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button 
                    color="default" 
                    variant="outlined" 
                    onClick={cancelEdit}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </Paper>
            ) : (
              <Paper key={review.id} style={{ margin: '0 0 16px 0', padding: '16px', position: 'relative' }} elevation={2}>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, review)}
                  style={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <MoreVert />
                </IconButton>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '4px' }}>
                  <Typography style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {review.stallName || `Stall #${review.stallId}`}
                  </Typography>
                  {[...Array(5)].map((_, idx) => {
                    const starValue = idx + 1;
                    if (review.rating >= starValue) {
                      return <Star key={idx} style={{ color: '#FFD700', fontSize: 18 }} />;
                    } else if (review.rating >= starValue - 0.5) {
                      return <Star key={idx} style={{ color: '#FFD700', fontSize: 18, opacity: 0.5 }} />;
                    } else {
                      return <StarBorder key={idx} style={{ color: '#BDBDBD', fontSize: 18 }} />;
                    }
                  })}
                  <Typography style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {review.rating.toFixed(1)}
                  </Typography>
                </div>
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '8px' }}>
                  {review.createdDate ? new Date(review.createdDate).toLocaleDateString() : ''}
                </Typography>
                <Typography variant="subtitle1" style={{ marginTop: 8, fontWeight: 'bold' }}>
                  {review.title}
                </Typography>
                <Typography style={{ marginTop: '4px' }}>
                  {review.description}
                </Typography>
              </Paper>
            )
          )}
        </div>
      </Drawer>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedReview && startEdit(selectedReview)}>
          <Edit fontSize="small" style={{ marginRight: '8px' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => selectedReview && openDeleteDialog(selectedReview)}>
          <Delete fontSize="small" style={{ marginRight: '8px' }} />
          Delete
        </MenuItem>
      </Menu>
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Review</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this review? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="default" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserReviewsSidebar;
